import React, { useState } from "react";
import { Mail, Phone, UserCircle, ChevronDown, ChevronUp } from "lucide-react";
import { formatPhone } from "@/utils/formatPhone";
import { TeamMember } from "@/api/teamMembers";

interface FloatingAgentCardProps {
  teamMember: TeamMember;
  primaryColor: string;
}

const FloatingAgentCard: React.FC<FloatingAgentCardProps> = ({
  teamMember,
  primaryColor,
}) => {
  const [isMinimized, setIsMinimized] = useState(true);

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <div className="fixed right-6 bottom-6 z-10 hidden lg:block">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden md:max-w-xs lg:max-w-sm xl:max-w-sm 2xl:max-w-md 3xl:max-w-md">
        {/* Header - Always visible */}
        <div
          className="flex items-center justify-between p-2 3xl:p-3 cursor-pointer"
          onClick={toggleMinimize}
          style={{
            background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}CC)`,
          }}
        >
          <div className="flex items-center gap-2 md:gap-3">
            <UserCircle className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            <h3 className="text-sm 3xl:text-lg font-semibold text-white">
              Your Dedicated Agent
            </h3>
          </div>
          <button
            className="text-white hover:bg-white/10 rounded-full p-1"
            aria-label={
              isMinimized ? "Expand agent card" : "Minimize agent card"
            }
          >
            {isMinimized ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Content - Collapsible */}
        <div
          className={`transition-all duration-300 ${
            isMinimized ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
          } overflow-hidden`}
        >
          <div className="p-4 xl:p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 rounded-full bg-gray-100">
                {teamMember.photo_url ? (
                  <img
                    src={teamMember.photo_url}
                    alt={`${teamMember.first_name} ${teamMember.last_name}`}
                    className="w-6 h-6 2xl:w-10 2xl:h-10 rounded-full object-cover"
                  />
                ) : (
                  <UserCircle
                    className="w-6 h-6 2xl:w-10 2xl:h-10 text-gray-500"
                    style={{ color: primaryColor }}
                  />
                )}
              </div>
              <div>
                <p className="text-base 3xl:text-lg font-semibold">
                  {teamMember.first_name} {teamMember.last_name}
                </p>
              </div>
            </div>

            <div className="mb-4">
              {teamMember.email && (
                <a
                  href={`mailto:${teamMember.email}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 underline mb-2"
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm 2xl:text-base">
                    {teamMember.email}
                  </span>
                </a>
              )}
              {teamMember.phone_number && (
                <a
                  href={`tel:${teamMember.phone_number}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 underline"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm 2xl:text-base">
                    {formatPhone(teamMember.phone_number)}
                  </span>
                </a>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <a
                href={`mailto:${teamMember.email}?subject=Real Estate Inquiry`}
                className="w-full text-white py-2 lg:py-2.5 xl:py-3 text-xs xl:text-sm rounded-lg hover:opacity-90 transition-colors text-center"
                style={{
                  background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}CC)`,
                }}
              >
                Email Agent
              </a>
              {teamMember.phone_number && (
                <a
                  href={`tel:${teamMember.phone_number}`}
                  className="w-full bg-white py-2 lg:py-2.5 xl:py-3 text-xs xl:text-sm rounded-lg hover:bg-gray-50 transition-colors border text-center"
                  style={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Call Agent
                </a>
              )}
            </div>

            <p className="text-gray-600 text-sm mt-4">
              Have questions? Need to request a tour? Contact your agent for any
              house-related inquiries.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingAgentCard;
